import CloseIcon from "@mui/icons-material/Close";
import {
  IconButton,
  Dialog as MuiDialog,
  Slide,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { motion, useDragControls } from "framer-motion";

import React, { useRef } from "react";

// Constants
const TABLET_SCREEN_SIZE = 768;

// Styled Dialog component
const StyledDialog = styled(MuiDialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    paddingTop: "24px",
    transition: "all 0.3s ease-in-out",
  },

  "&.MuiDialog-root": {
    alignItems: "center", // Center vertically
    justifyContent: "center", // Center horizontally
  },

  // Mobile styles
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    "& .MuiDialog-paper": {
      position: "absolute",
      bottom: 0,
      left: 0,
      right: 0,
      margin: 0,
      boxShadow: "0 -4px 12px rgba(0, 0, 0, 0.1)",
      paddingTop: "12px", // Add padding for the handle
    },
    "&.MuiDialog-root": {
      alignItems: "flex-end",
      width: "100%",
    },
  },
}));

// Drag handle for bottom sheet (mobile only)
const DragHandle = styled("div")(({ theme }) => ({
  width: "40px",
  height: "5px",
  backgroundColor: "#CCCCCC",
  borderRadius: "4px",
  margin: "0 auto",
  position: "absolute",
  top: "8px",
  left: "calc(50% - 20px)",
  zIndex: 10, // Ensure it's above other content
}));

// Slide transition for mobile (slides up from bottom)
const SlideUpTransition = React.forwardRef(function Transition(
  props: any,
  ref
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const StyledDialogPaper = styled(motion.div)(({ theme }) => ({
  background: "white",
  width: "fit-content",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  borderTopRightRadius: "10px",
  borderTopLeftRadius: "10px",
  borderBottomRightRadius: "10px",
  borderBottomLeftRadius: "10px",

  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    width: "100%",
    maxWidth: "100%",
    borderTopRightRadius: "8px",
    borderTopLeftRadius: "8px",
    borderBottomRightRadius: "0px",
    borderBottomLeftRadius: "0px",
  },
}));
// Wrap Dialog Paper in motion component
const MotionDialogPaper = React.forwardRef(function DialogPaper(
  props: any,
  ref
) {
  return (
    <StyledDialogPaper
      ref={ref}
      drag={props.drag}
      dragControls={props.dragControls}
      dragConstraints={props.dragConstraints}
      dragElastic={0.2} // More elasticity for better bounce feel
      dragTransition={{
        bounceStiffness: 300,
        bounceDamping: 30,
        timeConstant: 400,
      }} // Add spring physics
      onDragEnd={props.onDragEnd}
      {...props}
    />
  );
});

/**
 * ResponsiveDialog - A dialog component that behaves as a modal on desktop
 * and as a bottom sheet on mobile devices with swipe-to-dismiss functionality
 */
const ResponsiveDialog = ({
  open,
  onClose,

  children,
  ...dialogProps
}: {
  open: boolean;
  onClose?: () => void;
  children: React.ReactNode;
}) => {
  // Check if we're on mobile
  const isMobile = useMediaQuery(
    `@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`
  );

  // Choose the appropriate transition based on screen size
  const TransitionComponent = isMobile ? SlideUpTransition : undefined;

  // Create drag controls for the mobile bottom sheet
  const dragControls = useDragControls();

  // Ref for motion constraints
  const constraintsRef = useRef(null);

  // Start drag when user interacts with the handle
  const startDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!isMobile) return;
    dragControls.start(event);
  };

  // The full Dialog component with swipe-to-close functionality
  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      TransitionComponent={TransitionComponent}
      PaperComponent={MotionDialogPaper}
      PaperProps={{
        // Motion props for mobile swipe behavior
        ...(isMobile && {
          drag: "y",
          dragControls: dragControls,
          dragConstraints: { top: 0, bottom: 100 }, // Reduce bottom constraint for better bounce
          dragElastic: 0.2,
          dragTransition: {
            bounceStiffness: 600, // Higher value for more pronounced bounce
            bounceDamping: 30, // Lower value allows more bounce oscillation
            timeConstant: 300,
          },
          dragSnapToOrigin: true, // This is the key for auto bounce back!
          onDragEnd: (event: React.PointerEvent<HTMLDivElement>, info: any) => {
            // Close the dialog only if dragged down far enough or with enough velocity
            if (info.offset.y > 150 || info.velocity.y > 300) {
              onClose?.();
            }
            // Otherwise, it will automatically snap back to origin due to dragSnapToOrigin: true
          },
          ref: constraintsRef,
        }),
      }}
      {...dialogProps}
    >
      {isMobile ? (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            marginTop: "8px",
            height: "40px",
            touchAction: "none",
            zIndex: 1,
            cursor: "grab",
          }}
          onPointerDown={startDrag}
        >
          <DragHandle />
        </div>
      ) : onClose ? (
        <div
          style={{
            position: "absolute",
            top: 5,
            right: 5,
            height: "40px",
            width: "40px",
            touchAction: "none",
            zIndex: 1,
          }}
          onPointerDown={startDrag}
        >
          <IconButton aria-label="close" onClick={onClose} disableRipple>
            <CloseIcon
              sx={(theme) => ({ color: theme.palette.primary.main })}
            />
          </IconButton>
        </div>
      ) : null}

      {children}
    </StyledDialog>
  );
};

export { ResponsiveDialog };
